<template>
  <div class="usage-meter">
    <div class="flex items-baseline justify-between">
      <span class="text-neutral-text-strong font-bold">{{ label }}</span>
      <span v-if="isLoading" />
      <span v-else-if="used === null" class="text-neutral-text-weak font-bold">
        -- / --
      </span>
      <span v-else>
        <span :class="usedTextClass" class="font-bold">
          {{ formatNumberWithCommas(used) }}
        </span>
        <span v-if="limitText" class="text-neutral-text-weak ml-4">
          {{ limitText }}
        </span>
      </span>
    </div>
    <div
      v-if="isLoading"
      class="usage-meter__skeleton my-8 h-8 rounded-full bg-neutral-background animate-pulse"
    />
    <BittsProgressBar
      v-else-if="!isLimitMasked && used !== null && limit !== null"
      :width="width"
      :color="color"
      class="my-8"
    />
    <div class="flex items-center justify-between text-neutral-text-weak">
      <slot name="sub-line" />
      <slot name="action" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsProgressBar } from '@crossbeam/bitts';

import { computed } from 'vue';

import type { UsageStatusColor } from '@/composables/credits/useCreditLimits';
import { formatNumberWithCommas } from '@/utils';

const props = withDefaults(
  defineProps<{
    label: string;
    used: number | null;
    limit: number | null;
    percentText: string;
    width: string;
    color: UsageStatusColor;
    isLoading?: boolean;
    isLimitMasked?: boolean;
  }>(),
  { isLoading: false, isLimitMasked: false },
);

const usedTextClass = computed(
  () =>
    ({
      secondary: 'text-secondary-text',
      warning: 'text-warning-text',
      danger: 'text-danger-text',
    })[props.color],
);

const limitText = computed(() => {
  if (props.isLimitMasked) return '/ *';
  if (props.limit === null) return '';
  return `/ ${formatNumberWithCommas(props.limit)} (${props.percentText})`;
});
</script>
